import { useParams } from "react-router-dom";
import { CancelTask } from "../components/CancelTask";
import { Empty, List, Row } from "../components/List";
import { LogView } from "../components/LogView";
import { Facts, LivePage, Section } from "../components/Page";
import { Status } from "../components/Status";
import { Time } from "../components/Time";
import { repoLabel } from "../format";

export function Tasks() {
  return (
    <LivePage endpoint="tasks" title="Tasks">
      {(data) => (
        <List>
          {data.items.length === 0 ? (
            <Empty>No tasks have run yet.</Empty>
          ) : (
            data.items.map((t) => (
              <Row
                key={t.id}
                to={`/tasks/${encodeURIComponent(t.id)}`}
                title={t.prompt}
                end={<Status status={t.status} />}
                meta={[
                  t.id,
                  repoLabel(t.fork),
                  <Time key="t" iso={t.createdAt} />,
                  t.series ? `series ${t.series}` : "",
                ]}
              />
            ))
          )}
        </List>
      )}
    </LivePage>
  );
}

export function TaskDetail() {
  const { id = "" } = useParams();
  return (
    <LivePage
      endpoint={`tasks/${id}`}
      title="Task"
      qualifier={id}
      missing={{
        title: "No such task",
        note: "This id is not in the queue or the task store.",
      }}
    >
      {(data) => (
        <>
          <Facts
            items={[
              {
                key: "Status",
                // The one control on the page, and it sits here rather than in a toolbar
                // because "running" is the fact it acts on: it appears the moment that word
                // does and goes when it does. A task that is not running has nothing to stop.
                value: (
                  <span className="fact-inline">
                    <Status status={data.status} />
                    {data.status === "running" && <CancelTask id={data.id} />}
                  </span>
                ),
              },
              { key: "Fork", value: repoLabel(data.fork), data: true },
              { key: "Started", value: <Time iso={data.createdAt} />, data: true },
              // Only when the two differ: a queue entry and the run it became are numbered
              // separately, and the run's id is what names its log, its session and its record.
              ...(data.taskId !== null && data.taskId !== data.id
                ? [{ key: "Run", value: data.taskId, data: true }]
                : []),
              { key: "Log events", value: data.logTotal, data: true },
            ]}
          />

          <Section title="Prompt">
            <div className="panel">
              <pre className="pre">{data.prompt}</pre>
            </div>
          </Section>

          <Section
            title="Log"
            meta={data.logTruncated ? `last ${data.log.length} of ${data.logTotal}` : undefined}
          >
            <LogView
              events={data.log}
              total={data.logTotal}
              truncated={data.logTruncated}
              // Nothing to show means three different things, and only the status separates
              // them: a run whose log is missing, one that has not been picked up yet, and an
              // entry that reached a terminal status without ever starting one.
              empty={
                data.taskId !== null
                  ? "This task has no log file."
                  : data.status === "pending"
                    ? "Waiting for a worker. The trace appears here as the agent runs."
                    : "This entry never started a run, so there is no trace."
              }
            />
          </Section>
        </>
      )}
    </LivePage>
  );
}
